/** Personal persistence is always keyed by tenant and immutable account/guest identity. */
export interface StorageOwner {
  tenantId: string;
  userId: string;
  persistent: boolean;
}
export type SaveState = "saved" | "session" | "error";
let owner: StorageOwner = {
  tenantId: "unassigned",
  userId: "unassigned",
  persistent: false,
};
let saveState: SaveState = "session";
const listeners = new Set<() => void>();
const memory = new Map<string, string>();
const failedKeys = new Set<string>();
const prefix = "rf:v2:";

export function configureOwner(next: StorageOwner): void {
  if (!next.tenantId || !next.userId)
    throw new Error("A storage owner is required");
  owner = { ...next };
  setSaveState(
    [...failedKeys].some((key) => key.startsWith(ownerKey("")))
      ? "error"
      : next.persistent
        ? "saved"
        : "session",
  );
}
export function getStorageOwner(): StorageOwner {
  return { ...owner };
}
export function ownerKey(key: string, scope: StorageOwner = owner): string {
  return `${prefix}${encodeURIComponent(scope.tenantId)}:${encodeURIComponent(scope.userId)}:${key}`;
}
function backing(scope = owner): Storage {
  return scope.persistent ? localStorage : sessionStorage;
}
function setSaveState(next: SaveState) {
  saveState = next;
  listeners.forEach((listener) => listener());
}
export function getSaveState(): SaveState {
  return saveState;
}
export function subscribeSaveState(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
export const ownerStorage = {
  getItem(key: string, scope = owner): string | null {
    const scoped = ownerKey(key, scope);
    if (memory.has(scoped)) return memory.get(scoped) ?? null;
    try {
      return backing(scope).getItem(scoped);
    } catch {
      setSaveState("error");
      return null;
    }
  },
  setItem(key: string, value: string, scope = owner): boolean {
    const scoped = ownerKey(key, scope);
    memory.set(scoped, value); // Keep a recoverable copy even if browser storage is full.
    try {
      backing(scope).setItem(scoped, value);
      failedKeys.delete(scoped);
      if (scope.userId === owner.userId && scope.tenantId === owner.tenantId)
        setSaveState(
          [...failedKeys].some((key) => key.startsWith(ownerKey("")))
            ? "error"
            : scope.persistent
              ? "saved"
              : "session",
        );
      return true;
    } catch {
      failedKeys.add(scoped);
      if (scope.userId === owner.userId && scope.tenantId === owner.tenantId)
        setSaveState("error");
      return false;
    }
  },
  removeItem(key: string, scope = owner): void {
    const scoped = ownerKey(key, scope);
    memory.delete(scoped);
    failedKeys.delete(scoped);
    try {
      backing(scope).removeItem(scoped);
    } catch {
      setSaveState("error");
    }
  },
  clearOwner(): void {
    const scopePrefix = ownerKey("");
    for (const key of memory.keys())
      if (key.startsWith(scopePrefix) && !key.endsWith(":consent-revocations"))
        memory.delete(key);
    for (const storage of [localStorage, sessionStorage]) {
      try {
        const keys = Array.from({ length: storage.length }, (_, i) =>
          storage.key(i),
        );
        for (const key of keys)
          if (
            key?.startsWith(scopePrefix) &&
            !key.endsWith(":consent-revocations")
          )
            storage.removeItem(key);
      } catch {
        setSaveState("error");
      }
    }
  },
};
export function rotateGuestId(): string {
  const id = crypto.randomUUID();
  try {
    sessionStorage.setItem("rf:guest-id", id);
  } catch {
    /* In-memory session still works. */
  }
  return id;
}
export function getGuestId(): string {
  try {
    return sessionStorage.getItem("rf:guest-id") || rotateGuestId();
  } catch {
    return crypto.randomUUID();
  }
}
export function resetStorageMemoryForTests(): void {
  memory.clear();
  failedKeys.clear();
}

export function changeDevicePersistence(persistent: boolean): void {
  const old = getStorageOwner();
  const next = { ...old, persistent };
  const keys = new Set<string>();
  const oldBacking = backing(old);
  const pre = ownerKey("", old);
  for (let i = 0; i < oldBacking.length; i++) {
    const key = oldBacking.key(i);
    if (key?.startsWith(pre)) keys.add(key.slice(pre.length));
  }
  for (const key of memory.keys())
    if (key.startsWith(pre)) keys.add(key.slice(pre.length));
  keys.delete("consent-revocations");
  for (const key of keys) {
    const value = ownerStorage.getItem(key, old);
    if (value !== null && !ownerStorage.setItem(key, value, next))
      throw new Error("Could not move your data. Export before leaving.");
  }
  configureOwner(next);
  for (const key of keys) oldBacking.removeItem(ownerKey(key, old));
  localStorage.setItem(
    `rf:remember:${old.tenantId}:${old.userId}`,
    String(persistent),
  );
}
